import { useCallback, useEffect, useState } from "react";
import { DEFAULT_PUSH_API_ORIGIN } from "./types.js";
import { keyMatches, subscriptionKey } from "./ui/pushKey.js";
import { endpointTail, reconcilePlan } from "./ui/reconcilePlan.js";
import { clearPushState, readPushState, writePushState } from "./pushState.js";
import { isG1App } from "./ui/webview.js";
// One silent re-subscribe attempt per app per page load (set BEFORE acting, so a
// failure mid-way can't loop).
const reconciled = new Set();
async function fetchProviderKey(pushApiOrigin, appId) {
    const res = await fetch(`${pushApiOrigin}/sdk/provider?appId=${encodeURIComponent(appId)}`, {
        credentials: "include",
    });
    if (!res.ok)
        return null;
    const j = (await res.json());
    return j.data?.applicationServerKey ?? null;
}
async function registerSubscription(pushApiOrigin, appId, sub, oldEndpoint) {
    const res = await fetch(`${pushApiOrigin}/app/push/subscribe`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ appId, subscription: sub.toJSON(), ...(oldEndpoint ? { oldEndpoint } : {}) }),
    });
    if (!res.ok)
        throw new Error(`subscribe ${res.status}`);
}
/** Decode a base64url VAPID key to the Uint8Array pushManager.subscribe wants. */
function urlBase64ToUint8Array(base64) {
    const padding = "=".repeat((4 - (base64.length % 4)) % 4);
    const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(b64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++)
        out[i] = raw.charCodeAt(i);
    return out;
}
function isSupported() {
    return (typeof window !== "undefined" &&
        "serviceWorker" in navigator &&
        "PushManager" in window &&
        "Notification" in window &&
        // Inside the native G1 iOS shell push is the native layer's job (APNs
        // device token registered per application); WKWebView has no web push
        // anyway, and the bell must not offer a subscription that can't exist.
        !isG1App(navigator.userAgent));
}
/**
 * App-scoped web-push subscription for the operator shell. Feature-detects,
 * requests permission only on the user gesture that calls `subscribe()`, fetches
 * the app's VAPID key from g1-push `/sdk/provider?appId=`, subscribes via the
 * already-registered service worker, and registers the subscription with
 * `/app/push/subscribe` (cookie-authed, so it lands under the logged-in user).
 */
export function useAdminPush({ appId, pushApiOrigin = DEFAULT_PUSH_API_ORIGIN, }) {
    const supported = isSupported();
    const [permission, setPermission] = useState(supported ? Notification.permission : "unsupported");
    const [subscribed, setSubscribed] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    // Reflect an existing subscription on mount — and reconcile it with what the server was told (reconcilePlan):
    // provider key rotated → re-subscribe; endpoint differs from the stored one → re-register with oldEndpoint;
    // device was on but the subscription is gone (resetPwaApp / "Update to latest version" unregisters the SW and the
    // subscription dies with it) → subscribe again silently, permission is already granted. One action per page load.
    useEffect(() => {
        if (!supported)
            return;
        let cancelled = false;
        (async () => {
            const reg = await navigator.serviceWorker.ready;
            let sub = await reg.pushManager.getSubscription();
            if (!cancelled)
                setSubscribed(sub !== null);
            const stored = readPushState(appId);
            if (reconciled.has(appId) || (!sub && !stored))
                return;
            const key = await fetchProviderKey(pushApiOrigin, appId).catch(() => null);
            const plan = reconcilePlan({
                supported: true, permissionGranted: Notification.permission === "granted",
                stored: stored ? { id: appId, endpoint: stored.endpoint } : null,
                live: sub ? { endpoint: sub.endpoint, keyMatches: key ? keyMatches(subscriptionKey(sub), key) : null } : null,
            });
            if (plan === "skip" || plan === "match")
                return;
            reconciled.add(appId); // set BEFORE acting: a failure mid-way must not loop
            if (plan === "backfill" || plan === "reregister") {
                const live = sub;
                await registerSubscription(pushApiOrigin, appId, live, plan === "reregister" ? stored?.endpoint : undefined);
                writePushState(appId, live.endpoint);
                return;
            }
            if (!key)
                return; // provider unreachable → cannot subscribe, try next load
            const oldEndpoint = sub?.endpoint ?? stored?.endpoint;
            if (sub)
                await sub.unsubscribe().catch(() => undefined);
            sub = await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(key),
            });
            await registerSubscription(pushApiOrigin, appId, sub, oldEndpoint);
            writePushState(appId, sub.endpoint);
            if (!cancelled)
                setSubscribed(true);
            console.info("[pwa-kit] push reconciled", { appId, plan, live: endpointTail(sub.endpoint), was: endpointTail(oldEndpoint) });
        })().catch(() => { });
        return () => {
            cancelled = true;
        };
    }, [supported, appId, pushApiOrigin]);
    const subscribe = useCallback(async () => {
        if (!supported) {
            setError("Push not supported on this browser");
            return false;
        }
        setBusy(true);
        setError(null);
        try {
            const perm = await Notification.requestPermission();
            setPermission(perm);
            if (perm !== "granted") {
                setError(perm === "denied" ? "Notifications blocked" : "Permission not granted");
                return false;
            }
            const reg = await navigator.serviceWorker.ready;
            const key = await fetchProviderKey(pushApiOrigin, appId);
            if (!key)
                throw new Error("no VAPID key for app");
            let sub = await reg.pushManager.getSubscription();
            let oldEndpoint;
            // A subscription under a rotated-away key is useless (sends skip it):
            // replace it instead of re-registering it.
            if (sub && !keyMatches(subscriptionKey(sub), key)) {
                oldEndpoint = sub.endpoint;
                await sub.unsubscribe().catch(() => undefined);
                sub = null;
            }
            if (!sub) {
                sub = await reg.pushManager.subscribe({
                    userVisibleOnly: true,
                    // cast: TS lib types applicationServerKey as BufferSource<ArrayBuffer>;
                    // our decoded key is a plain Uint8Array, accepted at runtime.
                    applicationServerKey: urlBase64ToUint8Array(key),
                });
            }
            await registerSubscription(pushApiOrigin, appId, sub, oldEndpoint);
            writePushState(appId, sub.endpoint);
            setSubscribed(true);
            return true;
        }
        catch (e) {
            setError(e instanceof Error ? e.message : String(e));
            return false;
        }
        finally {
            setBusy(false);
        }
    }, [supported, appId, pushApiOrigin]);
    const unsubscribe = useCallback(async () => {
        if (!supported)
            return;
        setBusy(true);
        try {
            const reg = await navigator.serviceWorker.ready;
            const sub = await reg.pushManager.getSubscription();
            if (sub) {
                // Tell g1-push first: the browser-side unsubscribe alone leaves the
                // server row live and Apple keeps answering 201 for it forever.
                await fetch(`${pushApiOrigin}/app/push/subscribe`, {
                    method: "DELETE",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ appId, endpoint: sub.endpoint }),
                }).catch(() => undefined);
                await sub.unsubscribe();
            }
            clearPushState(appId);
            setSubscribed(false);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
        finally {
            setBusy(false);
        }
    }, [supported, appId, pushApiOrigin]);
    return { supported, permission, subscribed, busy, error, subscribe, unsubscribe };
}
//# sourceMappingURL=push.js.map